import { useLang } from "@/lib/i18n";
import { SITE } from "@/data/site";

const COPY = {
  en: {
    eyebrow: "Privacy Policy",
    title: "Privacy Policy",
    company: "Faster Plumbing",
    lastUpdated: "Last Updated: June 14, 2026",
    intro:
      'Faster Plumbing ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you contact us or use our services.',
    sections: [
      {
        h: "1. What Information We Collect",
        p: "We may collect the following types of personal information:",
        items: [
          "Name, phone number, email address, and service address",
          "Details about your plumbing issue or service request",
          "Payment information (processed securely via third-party processors)",
          "Device, browser, and IP address information when you visit our website",
          "Any information you voluntarily provide through forms, calls, or texts",
        ],
      },
      {
        h: "2. How We Use Your Information",
        p: "We use the information we collect to:",
        items: [
          "Schedule, confirm, and manage service appointments",
          "Send estimates, invoices, and job updates",
          "Communicate technician arrival times and service reminders",
          "Respond to inquiries and provide customer support",
          "Send promotional offers and seasonal specials (with your consent)",
          "Improve our website and the quality of our services",
          "Comply with applicable legal obligations",
        ],
      },
      {
        h: "3. SMS / Text Message Opt-In",
        paragraphs: [
          "By providing your phone number and opting in through our website form, booking page, or by texting a keyword to our business number, you consent to receive recurring automated SMS text messages from Faster Plumbing.",
          "These messages may include appointment confirmations and reminders, technician dispatch notifications, service estimates and updates, promotional offers, and review requests. Message frequency varies. Reply STOP to unsubscribe at any time. Reply HELP for help.",
        ],
      },
      {
        highlight:
          "Your SMS opt-in consent will never be sold, rented, or shared with any third party or affiliate for their own marketing purposes. This information is used solely by Faster Plumbing to communicate with you directly.",
      },
      {
        h: "4. Cookie & Tracking Info",
        p: "Our website uses cookies and similar tracking technologies to remember your preferences, analyze website traffic, and serve relevant advertisements. We may use tools such as Google Analytics and retargeting pixels. You may disable cookies through your browser settings, though some site features may be affected.",
      },
      {
        h: "5. Data Security Practices",
        p: "We implement industry-standard technical and organizational measures to protect your personal information from unauthorized access, disclosure, or alteration. These include SSL/TLS encryption, restricted internal data access, and secure third-party platforms for CRM and payment processing. No method of electronic transmission is 100% secure, but we take every reasonable precaution to protect your data.",
      },
      {
        h: "6. User Rights",
        p: "You have the right to:",
        items: [
          "Opt out of SMS messages at any time by replying STOP",
          "Request access to the personal information we hold about you",
          "Request correction of inaccurate information",
          "Request deletion of your personal data (subject to legal retention requirements)",
          'Opt out of marketing emails by clicking "Unsubscribe" in any email',
        ],
      },
      {
        h: "7. No Sharing of SMS Opt-In Data",
        p: "We do not sell, rent, or share your personal information — including SMS opt-in data and consent records — with third parties for marketing purposes. We may share data only with trusted service providers (e.g., scheduling platforms, payment processors) who are contractually required to protect your information and who assist us in operating our business.",
      },
      {
        h: "8. Changes to This Policy",
        p: "We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date. Continued use of our services after any update constitutes your acceptance of the revised policy.",
      },
    ],
    contactTitle: "Contact Us",
    contactIntro:
      "If you have questions about this Privacy Policy or wish to exercise your rights, contact us at:",
    coverageLine: "Lake Worth Beach, FL — Serving Palm Beach, Broward, Miami-Dade & Martin Counties",
    phoneLabel: "Phone",
    emailLabel: "Email",
    websiteLabel: "Website",
    rights: "All rights reserved.",
    relatedTerms: "Terms of Service",
  },
  es: {
    eyebrow: "Política de Privacidad",
    title: "Política de Privacidad",
    company: "Faster Plumbing",
    lastUpdated: "Última actualización: 14 de junio de 2026",
    intro:
      'Faster Plumbing ("nosotros", "nos" o "nuestro") se compromete a proteger su privacidad. Esta Política de Privacidad explica cómo recopilamos, usamos y protegemos su información cuando se contacta con nosotros o utiliza nuestros servicios.',
    sections: [
      {
        h: "1. Qué Información Recopilamos",
        p: "Podemos recopilar los siguientes tipos de información personal:",
        items: [
          "Nombre, número de teléfono, correo electrónico y dirección de servicio",
          "Detalles sobre su problema de plomería o solicitud de servicio",
          "Información de pago (procesada de forma segura por procesadores externos)",
          "Información del dispositivo, navegador y dirección IP cuando visita nuestro sitio web",
          "Cualquier información que proporcione voluntariamente a través de formularios, llamadas o mensajes",
        ],
      },
      {
        h: "2. Cómo Usamos Su Información",
        p: "Usamos la información recopilada para:",
        items: [
          "Agendar, confirmar y gestionar citas de servicio",
          "Enviar cotizaciones, facturas y actualizaciones del trabajo",
          "Comunicar tiempos de llegada del técnico y recordatorios de servicio",
          "Responder consultas y brindar atención al cliente",
          "Enviar ofertas promocionales y especiales de temporada (con su consentimiento)",
          "Mejorar nuestro sitio web y la calidad de nuestros servicios",
          "Cumplir con obligaciones legales aplicables",
        ],
      },
      {
        h: "3. Suscripción a Mensajes SMS / de Texto",
        paragraphs: [
          "Al proporcionar su número de teléfono y aceptar a través de nuestro formulario en línea, página de reservas o enviando una palabra clave a nuestro número comercial, usted consiente recibir mensajes SMS automatizados recurrentes de Faster Plumbing.",
          "Estos mensajes pueden incluir confirmaciones y recordatorios de citas, notificaciones de despacho de técnicos, cotizaciones y actualizaciones de servicio, ofertas promocionales y solicitudes de reseñas. La frecuencia de los mensajes varía. Responda STOP para cancelar la suscripción en cualquier momento. Responda HELP para ayuda.",
        ],
      },
      {
        highlight:
          "Su consentimiento de suscripción SMS nunca será vendido, alquilado ni compartido con terceros o afiliados para sus propios fines de marketing. Esta información es utilizada únicamente por Faster Plumbing para comunicarse con usted directamente.",
      },
      {
        h: "4. Cookies y Seguimiento",
        p: "Nuestro sitio web utiliza cookies y tecnologías de seguimiento similares para recordar sus preferencias, analizar el tráfico del sitio y mostrar anuncios relevantes. Podemos usar herramientas como Google Analytics y píxeles de remarketing. Puede deshabilitar las cookies desde la configuración de su navegador, aunque algunas funciones del sitio pueden verse afectadas.",
      },
      {
        h: "5. Prácticas de Seguridad de Datos",
        p: "Implementamos medidas técnicas y organizativas estándar de la industria para proteger su información personal contra acceso no autorizado, divulgación o alteración. Estas incluyen cifrado SSL/TLS, acceso interno restringido a datos y plataformas externas seguras para CRM y procesamiento de pagos. Ningún método de transmisión electrónica es 100% seguro, pero tomamos todas las precauciones razonables para proteger sus datos.",
      },
      {
        h: "6. Sus Derechos",
        p: "Usted tiene derecho a:",
        items: [
          "Cancelar los mensajes SMS en cualquier momento respondiendo STOP",
          "Solicitar acceso a la información personal que tenemos sobre usted",
          "Solicitar la corrección de información inexacta",
          "Solicitar la eliminación de sus datos personales (sujeto a requisitos legales de retención)",
          'Cancelar correos de marketing haciendo clic en "Cancelar suscripción" en cualquier correo',
        ],
      },
      {
        h: "7. No Compartimos Datos de Suscripción SMS",
        p: "No vendemos, alquilamos ni compartimos su información personal — incluidos los datos de suscripción SMS y los registros de consentimiento — con terceros para fines de marketing. Podemos compartir datos solo con proveedores de servicios de confianza (p. ej., plataformas de agendamiento, procesadores de pago) que están contractualmente obligados a proteger su información y que nos ayudan a operar nuestro negocio.",
      },
      {
        h: "8. Cambios en esta Política",
        p: "Podemos actualizar esta Política de Privacidad de vez en cuando. Los cambios se publicarán en esta página con una fecha de vigencia actualizada. El uso continuado de nuestros servicios después de cualquier actualización constituye su aceptación de la política revisada.",
      },
    ],
    contactTitle: "Contáctenos",
    contactIntro:
      "Si tiene preguntas sobre esta Política de Privacidad o desea ejercer sus derechos, contáctenos en:",
    coverageLine: "Lake Worth Beach, FL — Servimos Palm Beach, Broward, Miami-Dade y Martin",
    phoneLabel: "Teléfono",
    emailLabel: "Correo",
    websiteLabel: "Sitio web",
    rights: "Todos los derechos reservados.",
    relatedTerms: "Términos del Servicio",
  },
};

export default function Privacy() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;

  return (
    <>
      <section className="bg-brand-navy-dark text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-navy-dark via-brand-navy to-slate-900" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,191,0,0.15)_0%,transparent_55%)]" />
        </div>
        <div className="absolute -top-32 -right-24 w-[440px] h-[440px] rounded-full bg-brand-yellow/15 blur-3xl pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/70 to-transparent" />
        <div className="relative container-max py-16 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-yellow text-slate-900 px-3.5 py-1.5 text-xs font-bold tracking-wide shadow-lg shadow-yellow-300/20">
            {c.eyebrow}
          </div>
          <h1
            className="mt-5 font-heading text-4xl sm:text-5xl font-bold leading-tight"
            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.45)" }}
          >
            {c.title}
          </h1>
          <p className="mt-3 text-white/85 text-lg">
            <strong>{c.company}</strong> — {c.lastUpdated}
          </p>
        </div>
      </section>

      <section className="section-padding bg-slate-50">
        <div className="container-max max-w-3xl">
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-7 sm:p-10">
            <p className="text-slate-700 leading-relaxed">{c.intro}</p>

            <div className="mt-8 space-y-7">
              {c.sections.map((s, i) =>
                s.highlight ? (
                  <div
                    key={`hl-${i}`}
                    className="bg-brand-yellow/15 border-2 border-brand-yellow rounded-2xl px-5 py-4"
                  >
                    <p className="text-sm font-semibold text-slate-900 leading-relaxed flex items-start gap-2.5">
                      <span aria-hidden="true">🔒</span>
                      <span>{s.highlight}</span>
                    </p>
                  </div>
                ) : (
                  <div key={s.h}>
                    <h2 className="font-heading text-base font-bold text-slate-900 flex items-start gap-3">
                      <span className="mt-2 w-2 h-2 rounded-full bg-brand-yellow shrink-0" />
                      {s.h}
                    </h2>
                    {s.p && (
                      <p className="mt-2 text-slate-700 leading-relaxed pl-5">{s.p}</p>
                    )}
                    {s.paragraphs &&
                      s.paragraphs.map((para, idx) => (
                        <p
                          key={idx}
                          className={`text-slate-700 leading-relaxed pl-5 ${idx === 0 ? "mt-2" : "mt-2.5"}`}
                        >
                          {para}
                        </p>
                      ))}
                    {s.items && (
                      <ul className="mt-2 pl-5 space-y-1.5">
                        {s.items.map((it) => (
                          <li
                            key={it}
                            className="text-slate-700 leading-relaxed flex items-start gap-2.5"
                          >
                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )
              )}
            </div>

            {/* Contact card */}
            <div className="mt-10 bg-slate-50 border border-slate-200 rounded-2xl p-6">
              <h2 className="font-heading text-base font-bold text-slate-900">
                {c.contactTitle}
              </h2>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">{c.contactIntro}</p>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                <strong className="text-slate-900">{c.company}</strong>
                <br />
                {c.coverageLine}
                <br />
                {c.phoneLabel}:{" "}
                <a
                  href={`tel:${SITE.primaryPhone}`}
                  className="font-bold text-brand-yellow-dark hover:underline"
                >
                  {SITE.primaryPhone}
                </a>
                <br />
                {c.emailLabel}:{" "}
                <a
                  href={`mailto:${SITE.email}`}
                  className="font-bold text-brand-yellow-dark hover:underline"
                >
                  {SITE.email}
                </a>
                <br />
                {c.websiteLabel}:{" "}
                <a
                  href="https://fasterplumbing.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-brand-yellow-dark hover:underline"
                >
                  fasterplumbing.com
                </a>
              </p>
            </div>

            <p className="mt-9 text-xs text-slate-400 text-center">
              © {c.company}. {c.rights} ·{" "}
              <a href="/terms" className="text-slate-400 hover:text-brand-yellow-dark">
                {c.relatedTerms}
              </a>
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
